import React from 'react';
import { get } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { Input, TooltipComponent } from '@clarke-energia/foton-v2';
import { Controller, FieldPath, FieldValues, RegisterOptions, useFormContext } from 'react-hook-form';
import { NumberFormatProps } from 'react-number-format';
import { getFormFieldAttrs } from '@utils/form-field-helpers';

interface NumericFormFieldProps<T extends FieldValues> {
  field: FieldPath<T>;
  id: string;
  label: string;
  formatProps: Pick<
    NumberFormatProps,
    | 'placeholder'
    | 'prefix'
    | 'suffix'
    | 'allowNegative'
    | 'thousandSeparator'
    | 'decimalSeparator'
    | 'decimalScale'
    | 'fixedDecimalScale'
    | 'format'
    | 'mask'
    | 'isNumericString'
  >;
  options?: RegisterOptions<T>;
  className?: string;
  rootStyle?: string;
  error?: string;
  tooltipContent?: string;
  disabled?: boolean;
}

function NumericFormFieldComponent<T extends FieldValues>({
  field,
  id,
  label,
  formatProps: {
    placeholder,
    prefix,
    suffix,
    format,
    mask,
    allowNegative = false,
    thousandSeparator,
    decimalSeparator = ',',
    decimalScale = 2,
    fixedDecimalScale = true,
    isNumericString,
  },
  className,
  rootStyle,
  options,
  error,
  tooltipContent,
  disabled,
}: NumericFormFieldProps<T>) {
  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  const fieldError = error ? error : (get(errors, field)?.message as string | undefined);

  return (
    <Controller
      control={control}
      name={field}
      rules={options}
      render={({ field: { name, onChange, onBlur, value } }) => (
        <Input.Root className={rootStyle ?? 'w-full'}>
          {(label || tooltipContent) && (
            <span className="flex gap-4">
              <Input.Label className="!text-paragraph-small" content={label} required={options?.required as boolean} />
              {tooltipContent && (
                <TooltipComponent
                  icon={{
                    icon: 'InformationCircleIcon',
                    solid: true,
                  }}
                  side="right"
                  content={tooltipContent}
                />
              )}
            </span>
          )}
          <Input.NumericContent
            formatProps={{
              thousandSeparator: thousandSeparator,
              decimalSeparator: decimalSeparator,
              decimalScale: decimalScale,
              fixedDecimalScale: fixedDecimalScale,
              allowNegative: allowNegative,
              prefix: prefix,
              suffix: suffix,
              format: format,
              mask: mask,
              placeholder: placeholder,
            }}
            id={id}
            name={name}
            value={value}
            className={twMerge(
              '!h-7',
              className,
              disabled && '!bg-brand-gray-10 !border-2 !border-brand-gray-20 cursor-not-allowed',
            )}
            required={(options?.required as boolean) || false}
            error={fieldError}
            onBlur={onBlur}
            onChange={{
              valueArgType: isNumericString ? 'string' : 'number',
              callback: (value) => (options?.onChange ? options?.onChange(value) : onChange(value)),
            }}
            tooltipContent={tooltipContent}
            disabled={disabled}
            customAttrs={getFormFieldAttrs({ id, hasError: !!fieldError })}
          />
          {fieldError && <span className="text-paragraph-small text-orange-30">{fieldError}</span>}
        </Input.Root>
      )}
    />
  );
}

// Memoize com comparação profunda de props
export const NumericFormField = React.memo(NumericFormFieldComponent, (prevProps, nextProps) => {
  // Comparação customizada para memoization
  return (
    prevProps.field === nextProps.field &&
    prevProps.id === nextProps.id &&
    prevProps.label === nextProps.label &&
    prevProps.className === nextProps.className &&
    prevProps.rootStyle === nextProps.rootStyle &&
    prevProps.error === nextProps.error &&
    prevProps.tooltipContent === nextProps.tooltipContent &&
    prevProps.disabled === nextProps.disabled &&
    JSON.stringify(prevProps.formatProps) === JSON.stringify(nextProps.formatProps)
  );
}) as typeof NumericFormFieldComponent;
